import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wallet, Receipt } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { TableSkeleton } from '@/components/common/TableSkeleton'
import { EmptyState } from '@/components/common/EmptyState'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { Button } from '@/components/ui/Button'
import { useMyCurrentSlip, useMyPayrollHistory } from '@/features/payroll/hooks/usePayrollMe'
import { useMyLive } from '@/features/payroll/hooks/usePayrollLive'
import { StatusBadge } from '@/features/payroll/components/StatusBadge'
import { StatCard } from '@/features/payroll/components/StatCard'
import type { PayrollHistoryEntry } from '@/features/payroll/types/payroll.types'

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function LiveEarningsCard(): React.ReactElement | null {
  const { data, isLoading } = useMyLive()
  if (isLoading || !data) return null

  const available = data.companies.filter((c) => c.available)
  if (available.length === 0) return null

  // Single-currency orgs get the combined figure; otherwise show the
  // first company's numbers per row below.
  const summary = data.combined ?? {
    currency: available[0].currency,
    regularHours: available[0].regularHours,
    overtimeHours: available[0].overtimeHours,
    totals: {
      gross: available[0].totals.gross ?? 0,
      netPay: available[0].totals.netPay ?? 0,
      totalTax: available[0].totals.totalTax ?? 0,
    },
  }
  const clockedIn = available.some((c) => c.clockedIn)
  const first = available[0]
  const period = `${MONTH_NAMES[first.payrollMonth.month - 1]} ${first.payrollMonth.year}`

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Earned so far — {period}
          </p>
          {clockedIn && (
            <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
              <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
              Clocked in
            </span>
          )}
        </div>
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
          Estimate
        </span>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <StatCard
          label={`Hours (${summary.regularHours}h${summary.overtimeHours ? ` + ${summary.overtimeHours}h OT` : ''})`}
          value={summary.totals.gross}
          currency={summary.currency}
        />
        <StatCard label="Est. Tax" value={summary.totals.totalTax} currency={summary.currency} />
        <StatCard
          label="Est. Net"
          value={summary.totals.netPay}
          currency={summary.currency}
          className="border-green-200 dark:border-green-900/50"
        />
      </div>
      <p className="mt-3 text-xs text-gray-400">
        Estimated from your hours and records so far. Final amounts come from the
        monthly payroll run.
      </p>
    </div>
  )
}

export function MyPayrollHomePage(): React.ReactElement {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)

  const { data: current, isLoading: currentLoading } = useMyCurrentSlip()
  const { data: historyData, isLoading: historyLoading } = useMyPayrollHistory(page)

  const history = historyData?.data ?? []
  const total = historyData?.total ?? 0
  const limit = historyData?.limit ?? 10

  return (
    <PageShell
      breadcrumbItems={[{ label: 'My Payroll' }]}
      icon={Wallet}
      title="My Payroll"
      description="Your salary slips and payroll history"
      className="gap-0"
      hideHeader
    >
      <div className="space-y-8">
        {/* Live month-to-date earnings */}
        <LiveEarningsCard />

        {/* Current slip card */}
        {currentLoading ? (
          <div className="animate-pulse rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="h-4 w-32 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="mt-3 h-8 w-48 rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        ) : current?.hasData && (current.slipId ?? current.batchId) ? (
          <div className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Latest Payslip — {current.period}
                </p>
                {current.paidAt && (
                  <p className="mt-1 text-xs text-gray-400">
                    Paid {new Date(current.paidAt).toLocaleDateString()}
                  </p>
                )}
              </div>
              {current.status && <StatusBadge status={current.status} />}
            </div>
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Gross" value={current.gross ?? 0} currency={current.currency} />
              <StatCard label="Tax" value={current.tax ?? 0} currency={current.currency} />
              <StatCard
                label="Net Pay"
                value={current.net ?? 0}
                currency={current.currency}
                className="border-green-200 dark:border-green-900/50"
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="mt-4"
              onClick={() => navigate(`/payroll/me/slips/${current.slipId ?? current.batchId}`)}
            >
              <Receipt className="mr-1.5 h-3.5 w-3.5" />
              View Details
            </Button>
          </div>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 p-6 text-center dark:border-gray-700 dark:bg-gray-800/30">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your latest payroll slip will appear here once processed.
            </p>
          </div>
        )}

        {/* History */}
        <div>
          <h3 className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
            Payroll History
          </h3>
          {historyLoading ? (
            <TableSkeleton rows={5} cols={5} />
          ) : history.length === 0 ? (
            <EmptyState icon={Wallet} title="No payroll history available yet" />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                      <th className="px-4 py-3">Period</th>
                      <th className="px-4 py-3 text-right">Gross</th>
                      <th className="px-4 py-3 text-right">Tax</th>
                      <th className="px-4 py-3 text-right">Net</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((entry: PayrollHistoryEntry) => (
                      <tr
                        key={entry.slipId}
                        className="cursor-pointer border-b border-gray-100 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700/30"
                        onClick={() => navigate(`/payroll/me/slips/${entry.slipId}`)}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                          {entry.period}
                          {entry.slipNumber && (
                            <span className="block text-xs font-normal text-gray-500 dark:text-gray-400">
                              {entry.slipNumber}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {(entry.gross ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">
                          {(entry.tax ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium">
                          {(entry.net ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={entry.status} />
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500">
                          {entry.paidAt ? new Date(entry.paidAt).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {total > limit && (
                <TablePaginationBar
                  page={page}
                  limit={limit}
                  total={total}
                  totalPages={Math.ceil(total / limit)}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </PageShell>
  )
}

MyPayrollHomePage.displayName = 'MyPayrollHomePage'
